import styles from './ChangeAnalysisPage.module.scss';
import React, {useEffect, useState} from 'react';
import {subsManager} from "../../../../store/middleware/subs-manager/subs-manager";
import {
    useActiveProjectCalendarsSelector,
    useActiveProjectSelector,
    useCpmMapSelector,
    useProjectVersionsSelector
} from "../../../../store/selectors/project.selectors";
import VersionChart from "./components/VersionChart";
import {calculateChangeToTasks} from "../../../../utils/version-control/calculateChangeToTasks";
import {useAllTaskListSelector} from "../../../../store/selectors/task/task.selectors";
import { reuleaux } from 'ldrs'
import moment from "moment";
import {ChangeListSection} from "./components/ChangeList";
import { faFileXls} from "@fortawesome/pro-light-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import exportChangeToExcel from "../../../../utils/version-control/exportChangeToExcel";
reuleaux.register()

const generateName = (doc) => {
    if (doc.data.projectId) {
        return `Strategy ${doc.data.versionName} (${moment(doc.data.versionTimestamp.toDate()).format('DD-MM-YYYY HH:mm')})`;
    } else {
        return `Sprint update (${moment(doc.data.versionTimestamp.toDate()).format('DD-MM-YYYY HH:mm')})`;
    }
}

const initialNameGeneration = (doc) => {
    if (doc.projectId) {
        return `Strategy ${doc.versionName} (${moment(doc.versionTimestamp.toDate()).format('DD-MM-YYYY HH:mm')})`;
    } else {
        return `Current project (${moment(doc.versionTimestamp.toDate()).format('DD-MM-YYYY HH:mm')})`;
    }
}

export default function ChangeAnalysisPage() {
    const [baselineDetails, setBaselineDetails] = useState<any>(null);
    const [comparatorDetails, setComparatorDetails] = useState<any>(null);
    const activeProject = useActiveProjectSelector();
    const projectVersions = useProjectVersionsSelector();
    const [changeData, setChangeData] = useState<any>(null);
    const allTasks = useAllTaskListSelector();
    const [loading, setLoading] = useState<boolean>(true);
    const [prevBaselineIndex, setPrevBaselineIndex] = useState<number | null>(null);
    const [prevComparatorIndex, setPrevComparatorIndex] = useState<number | null>(null);
    const [topOfContent, setTopOfContent] = useState<number>(250);
    const cpmMap = useCpmMapSelector();
    const calendarMap = useActiveProjectCalendarsSelector();

    useEffect(() => {
        const content = document.getElementById('change-analysis-results');
        if (content) {
            const rect = content.getBoundingClientRect();
            setTopOfContent(rect.top);
        }
    }, []);

    useEffect(() => {
        if (activeProject) subsManager.subscribeProjectVersions(activeProject.contractId, activeProject.projectId);
    }, [activeProject]);

    useEffect(() => {
        if (projectVersions.length > 0 && !changeData && activeProject) {
            const newProject = projectVersions[projectVersions.length - 1]
            const oldProject = projectVersions[0]
            calculateChangeToTasks(
                newProject.projectId,
                newProject.dataVersionId,
                oldProject.projectId,
                oldProject.dataVersionId,
                activeProject!.contractId,
                cpmMap,
                calendarMap,
                allTasks
            ).then((data) => {
                setChangeData(data)
                setLoading(false);
                setPrevBaselineIndex(0);
                setPrevComparatorIndex(projectVersions.length - 1);
            });
        }
    }, [projectVersions]);

    useEffect(() => {
        if (baselineDetails) {
            if (comparatorDetails) {
                if ((prevBaselineIndex !== null && prevBaselineIndex !== baselineDetails.x) ||
                    (prevComparatorIndex !== null && prevComparatorIndex !== comparatorDetails.x)) {
                    setLoading(true);
                    calculateChangeToTasks(
                        comparatorDetails.data.projectId,
                        comparatorDetails.data.dataVersionId,
                        baselineDetails.data.projectId,
                        baselineDetails.data.dataVersionId,
                        activeProject!.contractId,
                        cpmMap,
                        calendarMap,
                        undefined
                    ).then((data) => {
                        setChangeData(data)
                        setLoading(false);
                        setPrevBaselineIndex(baselineDetails.x);
                        setPrevComparatorIndex(comparatorDetails.x);
                    });
                }
            } else {
                const newProject = projectVersions[projectVersions.length - 1]
                if (prevBaselineIndex !== null &&
                    prevBaselineIndex !== baselineDetails.x) {
                    setLoading(true);
                    calculateChangeToTasks(
                        newProject.projectId,
                        newProject.dataVersionId,
                        baselineDetails.data.projectId,
                        baselineDetails.data.dataVersionId,
                        activeProject!.contractId,
                        cpmMap,
                        calendarMap,
                        allTasks
                    ).then((data) => {
                        setChangeData(data)
                        setLoading(false);
                        setPrevBaselineIndex(baselineDetails.x);
                    });
                }
            }
        } else if (comparatorDetails) {
            const oldProject = projectVersions[0]
            if (prevComparatorIndex !== null &&
                prevComparatorIndex !== comparatorDetails.x) {
                setLoading(true);
                calculateChangeToTasks(
                    comparatorDetails.data.projectId,
                    comparatorDetails.data.dataVersionId,
                    oldProject.projectId,
                    oldProject.dataVersionId,
                    activeProject!.contractId,
                    cpmMap,
                    calendarMap,
                    undefined
                ).then((data) => {
                    setChangeData(data)
                    setLoading(false);
                    setPrevComparatorIndex(comparatorDetails.x);
                });
            }
        }
    }, [baselineDetails, comparatorDetails]);

    return (
        <div className={styles.ChangeAnalysisPage}>
            <div className={styles.BaselineSelector}>
                {projectVersions.length > 0 &&
                    <VersionChart
                        width={window.innerWidth}
                        height={90}
                        chartData={projectVersions}
                        baseline={baselineDetails}
                        comparator={comparatorDetails}
                        setBaseline={setBaselineDetails}
                        setComparator={setComparatorDetails}
                    />}
            </div>
            {projectVersions.length &&
                <div className={styles.ComparisonHeader}>
                    <span className={styles.ActionButtons}>
                        <div
                            className={changeData ? styles.ActionButton : styles.ActionButtonDisabled}
                            onClick={() => exportChangeToExcel(changeData, activeProject!)}
                        >
                            <FontAwesomeIcon
                                icon={faFileXls}
                                style={{marginRight: '5px'}}
                            />
                            Export to Excel
                        </div>
                    </span>
                    {`Comparing Baseline: ${baselineDetails ? generateName(baselineDetails) : initialNameGeneration(projectVersions[0])} 
                    to ${comparatorDetails ? generateName(comparatorDetails) : initialNameGeneration(projectVersions[projectVersions.length - 1])}`}
                </div>}
                {loading ?
                        <div className={styles.AnalysisLoading}>
                        <l-reuleaux
                        size="60"
                        stroke="6"
                        stroke-length="0.15"
                        bg-opacity="0.1"
                        speed="1.2"
                        color="black"
                    ></l-reuleaux><h3 style={{padding: 0}}>Loading...</h3></div> :
                changeData ?
                    <div
                        className={styles.AnalysisResults}
                        id="change-analysis-results"
                        style={{maxHeight: `calc(100vh - ${topOfContent}px - 25px)`, overflowY: 'auto'}}
                    >
                        {/*<div className={styles.ChangeLists}>*/}
                            {changeData.newTasks.length !== 0 && <ChangeListSection
                                title="New Tasks"
                                changeList={changeData.newTasks}
                                total={changeData.totalOldTasks}
                                headline="New Tasks Added"
                                text="Tasks that have been added in the comparator version which were not present in the baseline version."
                            />}
                            {changeData.deletedTasks.length !== 0 && <ChangeListSection
                                title="Deleted Tasks"
                                changeList={changeData.deletedTasks}
                                total={changeData.totalOldTasks}
                                headline="Tasks Deleted"
                                text="Tasks that were present in the baseline version but have been removed in the comparator version."
                            />}
                            {changeData.taskNameChanges.length !== 0 && <ChangeListSection
                                title="Task Name Changes"
                                changeList={changeData.taskNameChanges}
                                total={changeData.totalOldTasks}
                                headline="Task Name Changes"
                                text="Tasks that have had their names modified between the baseline and comparator versions."
                            />}
                            {changeData.taskWBSChanges.length !== 0 && <ChangeListSection
                                title="Task WBS Changes"
                                changeList={changeData.taskWBSChanges}
                                total={changeData.totalOldTasks}
                                headline="Task WBS Changes"
                                text="Tasks that have had their Work Breakdown Structure (WBS) codes modified between the baseline and comparator versions."
                            />}
                            {changeData.taskDurationChanges.length !== 0 && <ChangeListSection
                                title="Task Duration Changes"
                                changeList={changeData.taskDurationChanges}
                                total={changeData.totalOldTasks}
                                headline="Task Duration Changes"
                                text="Tasks that have had their durations modified between the baseline and comparator versions."
                            />}
                            {changeData.tasksDelayed.length !== 0 && <ChangeListSection
                                title="Tasks Delayed"
                                changeList={changeData.tasksDelayed}
                                total={changeData.totalOldTasks}
                                headline="Tasks Delayed"
                                text="Tasks that are scheduled to finish later in the comparator version compared to the baseline version."
                            />}
                            {changeData.tasksAhead.length !== 0 && <ChangeListSection
                                title="Tasks Ahead"
                                changeList={changeData.tasksAhead}
                                total={changeData.totalOldTasks}
                                headline="Tasks Ahead"
                                text="Tasks that are scheduled to finish earlier in the comparator version compared to the baseline version."
                            />}
                            {changeData.logicChanges.length !== 0 && <ChangeListSection
                                title="Logic Changes"
                                changeList={changeData.logicChanges}
                                total={changeData.totalOldTasks}
                                headline="Task Logic Changes"
                                text="Changes in task dependencies and relationships between the baseline and comparator versions."
                            />}
                            {changeData.tasksStarted.length !== 0 && <ChangeListSection
                                title="Tasks Started"
                                changeList={changeData.tasksStarted}
                                total={changeData.totalOldTasks}
                                headline="Tasks Started"
                                text="Tasks that have commenced in the comparator version but were not started in the baseline version."
                            />}
                            {changeData.tasksCompleted.length !== 0 && <ChangeListSection
                                title="Tasks Completed"
                                changeList={changeData.tasksCompleted}
                                total={changeData.totalOldTasks}
                                headline="Tasks Completed"
                                text="Tasks that have been completed in the comparator version but were not completed in the baseline version."
                            />}
                        </div>
                    // </div>
                    : <div>No changes to display</div>}
        </div>
    )
}